import { useEffect, useState, useCallback } from 'react';
import { automationApi, type QueueStatus } from '@/lib/adminApi';
import { toast, Toaster } from 'sonner';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { StatCard, PageHeader, Skeleton, EmptyState } from '@/components/ui/page';
import { Button } from '@/components/ui/button';
import { RefreshCw, Layers, CheckCircle, AlertTriangle, Clock, PlayCircle } from 'lucide-react';

const KNOWN_QUEUES = ['order-lifecycle', 'ai-conversation', 'upsell-engine', 'shipping-jobs', 'recovery-jobs', 'campaign-delivery', 'campaign-scheduler', 'whatsapp-outbound', 'ai-requests', 'workflow-executions'];

export default function AutomationQueuePage() {
  const [queues, setQueues] = useState<QueueStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await automationApi.queueStatus();
      if (res.success) setQueues(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    if (!autoRefresh) return;
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [load, autoRefresh]);

  const totals = queues.reduce((acc, q) => ({
    waiting: acc.waiting + (q.waiting || 0),
    active: acc.active + (q.active || 0),
    completed: acc.completed + (q.completed || 0),
    failed: acc.failed + (q.failed || 0),
    delayed: acc.delayed + (q.delayed || 0),
  }), { waiting: 0, active: 0, completed: 0, failed: 0, delayed: 0 });

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <PageHeader title="Queue Monitor" description="BullMQ job queue status" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Toaster position="top-center" richColors />
      <PageHeader
        title="Queue Monitor"
        description="BullMQ job queue status — refreshes every 5s"
        action={
          <div className="flex gap-2">
            <Button size="sm" variant={autoRefresh ? 'default' : 'outline'} onClick={() => setAutoRefresh(!autoRefresh)}>
              {autoRefresh ? 'Auto' : 'Manual'}
            </Button>
            <Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
          </div>
        }
      />

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Waiting" value={totals.waiting} />
        <StatCard label="Active" value={totals.active} />
        <StatCard label="Completed" value={totals.completed.toLocaleString()} />
        <StatCard label="Failed" value={totals.failed} />
        <StatCard label="Delayed" value={totals.delayed} />
      </div>

      {/* Per-queue detail */}
      <Card>
        <CardHeader title="Queue Details" description={`${queues.length} queues`} />
        <CardBody>
          {queues.length === 0 ? (
            <EmptyState title="No queue data available. Ensure worker process is running." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-zinc-500 uppercase border-b border-zinc-800">
                    <th className="text-left py-2 px-2">Queue</th>
                    <th className="text-right py-2 px-2">Waiting</th>
                    <th className="text-right py-2 px-2">Active</th>
                    <th className="text-right py-2 px-2">Completed</th>
                    <th className="text-right py-2 px-2">Failed</th>
                    <th className="text-right py-2 px-2">Delayed</th>
                    <th className="text-center py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {queues.map(q => {
                    const hasFailures = (q.failed || 0) > 0;
                    const isBusy = (q.active || 0) > 0;
                    return (
                      <tr key={q.queue} className="border-b border-zinc-800/60 hover:bg-zinc-900/40">
                        <td className="py-2 px-2 font-medium font-mono text-xs">{q.queue}</td>
                        <td className="py-2 px-2 text-right">{q.waiting || 0}</td>
                        <td className="py-2 px-2 text-right">{q.active || 0}</td>
                        <td className="py-2 px-2 text-right text-zinc-400">{q.completed?.toLocaleString() || 0}</td>
                        <td className="py-2 px-2 text-right">
                          {hasFailures ? <span className="text-red-400">{q.failed}</span> : <span className="text-zinc-500">0</span>}
                        </td>
                        <td className="py-2 px-2 text-right">{q.delayed || 0}</td>
                        <td className="py-2 px-2 text-center">
                          {hasFailures ? <AlertTriangle className="h-3.5 w-3.5 text-red-400 mx-auto" /> :
                           isBusy ? <PlayCircle className="h-3.5 w-3.5 text-emerald-400 mx-auto" /> :
                           <CheckCircle className="h-3.5 w-3.5 text-zinc-600 mx-auto" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Known queues not yet reporting */}
      {queues.length > 0 && (
        <div className="text-xs text-zinc-600 text-center">
          {KNOWN_QUEUES.filter(k => !queues.find(q => q.queue === k)).length > 0 && (
            <span>Queues not yet reporting: {KNOWN_QUEUES.filter(k => !queues.find(q => q.queue === k)).join(', ')}</span>
          )}
        </div>
      )}
    </div>
  );
}
